// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { ReactNode } from 'react';
import './DataTable.css';

export interface DataTableColumn<T> {
  /** Unique key for this column */
  key: string;
  /** Column header text */
  header: string;
  /** Whether the column is sortable */
  sortable?: boolean;
  /** Fixed width (e.g. '120px'). If omitted, column flexes to fill. */
  width?: string;
  /** Render function for cell content */
  render: (row: T, index: number) => ReactNode;
}

export interface DataTableProps<T> {
  /** Column definitions */
  columns: DataTableColumn<T>[];
  /** Row data */
  data: T[];
  /** Unique key extractor for each row */
  rowKey: (row: T, index: number) => string;
  /** Currently sorted column key */
  sortColumn?: string;
  /** Sort direction */
  sortDirection?: 'asc' | 'desc';
  /** Called when a sortable column header is clicked */
  onSort?: (column: string) => void;
  /** Called when a row is clicked */
  onRowClick?: (row: T, index: number) => void;
  /** Whether the table is loading */
  loading?: boolean;
  /** Number of skeleton rows to show when loading */
  skeletonRows?: number;
  /** Content to display when data is empty */
  emptyState?: ReactNode;
  /** Additional CSS class */
  className?: string;
}

export function DataTable<T>({
  columns,
  data,
  rowKey,
  sortColumn,
  sortDirection = 'asc',
  onSort,
  onRowClick,
  loading = false,
  skeletonRows = 5,
  emptyState,
  className = '',
}: DataTableProps<T>) {
  return (
    <div className={`fin-data-table ${className}`}>
      <div className="fin-data-table__wrapper">
        <table className="fin-data-table__table">
          <thead>
            <tr className="fin-data-table__header-row">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`fin-data-table__header-cell ${col.sortable ? 'fin-data-table__header-cell--sortable' : ''}`}
                  style={col.width ? { width: col.width, minWidth: col.width } : undefined}
                  onClick={col.sortable && onSort ? () => onSort(col.key) : undefined}
                >
                  <span className="fin-data-table__header-text">{col.header}</span>
                  {col.sortable && (
                    <span className="fin-data-table__sort-icon">
                      {sortColumn === col.key
                        ? sortDirection === 'asc' ? '▲' : '▼'
                        : '▾'}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading &&
              Array.from({ length: skeletonRows }).map((_, i) => (
                <tr key={`skeleton-${i}`} className="fin-data-table__row">
                  {columns.map((col) => (
                    <td key={col.key} className="fin-data-table__cell">
                      <div className="fin-data-table__skeleton" />
                    </td>
                  ))}
                </tr>
              ))}

            {!loading && data.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="fin-data-table__empty">
                  {emptyState || <span>No data found.</span>}
                </td>
              </tr>
            )}

            {!loading &&
              data.map((row, index) => (
                <tr
                  key={rowKey(row, index)}
                  className={`fin-data-table__row ${onRowClick ? 'fin-data-table__row--clickable' : ''}`}
                  onClick={onRowClick ? () => onRowClick(row, index) : undefined}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className="fin-data-table__cell"
                      style={col.width ? { width: col.width, minWidth: col.width } : undefined}
                    >
                      {col.render(row, index)}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
